// @flow
import * as React from 'react';
import classnames from 'classnames';
import { Modal, AutoComplete } from 'antd';
import { DeleteOutlined, SaveOutlined, AppstoreAddOutlined, EditOutlined } from '@ant-design/icons';
import styles from './ProcurementRow.css';
import SaveToCatalogModal from './SaveToCatalogModal';

type Props = {
  item: any,
  suppliers: [],
  editable: boolean,
  onChangeField: Function,
  onBlurField: Function,
  onClickDelete: Function,
}

type State = {
  focusedField: null|string,
}

export default class extends React.PureComponent<Props, State> {

  state = {
    focusedField: null,
    showSaveToCatalog: false,
  }

  onChangeField = (field: string, value: any) => {
    const { item, onChangeField } = this.props;
    onChangeField(item.id, field, value);
  }

  onBlurField = (field: string) => {
    const { item, onBlurField } = this.props;
    onBlurField(item.id, field);
    this.setState({ focusedField: null });
  }

  onSelectSupplier = (value: string, option: any) => {
    const { item, onChangeField } = this.props;
    // When selecting from dropdown, set both supplier_id and supplier_name
    if (option && option.supplierId) {
      onChangeField(item.id, 'supplier_id', option.supplierId);
      onChangeField(item.id, 'supplier_name', value);
    } else {
      // Free text entry
      onChangeField(item.id, 'supplier_id', null);
      onChangeField(item.id, 'supplier_name', value);
    }
  }

  onChangeSupplierText = (value: string) => {
    const { item, onChangeField } = this.props;
    // When typing, update supplier_name and clear supplier_id
    onChangeField(item.id, 'supplier_name', value);
    onChangeField(item.id, 'supplier_id', null);
  }

  formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `£${parseFloat(value).toFixed(2)}`;
  }

  getStatusColorClass = (status) => {
    const statusLower = (status || 'pending').toLowerCase();
    switch (statusLower) {
      case 'pending':
        return styles.statusPending;
      case 'ordered':
        return styles.statusOrdered;
      case 'partial':
        return styles.statusPartial;
      case 'received':
        return styles.statusReceived;
      case 'cancelled':
        return styles.statusCancelled;
      default:
        return styles.statusPending;
    }
  }

  calculateTotalCost = () => {
    const { item } = this.props;
    const qty = parseFloat(String(item.quantity || 0).replace(/,/g, '')) || 0;
    const unitCost = parseFloat(String(item.unit_cost || 0).replace(/,/g, '')) || 0;
    const taxRate = parseFloat(String(item.tax_rate || 0).replace(/,/g, '')) || 0;
    const subtotal = qty * unitCost;
    return (subtotal * (1 + taxRate / 100)).toFixed(2);
  }

  calculateTotalSellPrice = () => {
    const { item } = this.props;
    const totalCost = parseFloat(this.calculateTotalCost());
    const markup = parseFloat(item.markup_percentage || 0);
    return (totalCost * (1 + markup / 100)).toFixed(2);
  }

  onClickDelete = () => {
    const { item, onClickDelete } = this.props;
    Modal.confirm({
      title: 'Delete Procurement Item',
      content: `Are you sure you want to delete "${item.item_name || 'this item'}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => onClickDelete(item),
    });
  }

  onClickUndoDelete = () => {
    const { item, onChangeField } = this.props;
    const { _pendingDelete, ...rest } = item;
    onChangeField(item.id, '_pendingDelete', false);
  }

  render() {
    const { item, suppliers, editable } = this.props;
    const { focusedField } = this.state;

    const totalCost = this.calculateTotalCost();
    const totalSellPrice = this.calculateTotalSellPrice();

    return (
      <React.Fragment>
      <div className={styles.wrapper} style={item._pendingDelete ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
        <div className={styles.table}>
          {/* Supplier */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'supplier_name' })} style={{ width: 150 }}>
            {editable ? (
              <AutoComplete
                className={styles.autoComplete}
                value={item.supplier_name || ''}
                onChange={this.onChangeSupplierText}
                onSelect={this.onSelectSupplier}
                onFocus={() => this.setState({ focusedField: 'supplier_name' })}
                onBlur={() => this.onBlurField('supplier_name')}
                placeholder="Supplier name"
                style={{ width: '100%', height: '100%' }}
                options={suppliers.map(supplier => ({
                  value: supplier.company_name,
                  label: supplier.company_name,
                  supplierId: supplier.id,
                }))}
                filterOption={(inputValue, option) =>
                  option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            ) : (
              <div className={styles.outlet}>{item.supplier_name || '-'}</div>
            )}
          </div>

          {/* Item Name */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'item_name' })} style={{ width: 200 }}>
            <input
              className={styles.input}
              value={item.item_name || ''}
              onChange={event => this.onChangeField('item_name', event.target.value)}
              onFocus={() => this.setState({ focusedField: 'item_name' })}
              onBlur={() => this.onBlurField('item_name')}
              disabled={!editable}
              placeholder="Item name"
            />
          </div>

          {/* Item Code */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'item_code' })} style={{ width: 100 }}>
            <input
              className={styles.input}
              value={item.item_code || ''}
              onChange={event => this.onChangeField('item_code', event.target.value)}
              onFocus={() => this.setState({ focusedField: 'item_code' })}
              onBlur={() => this.onBlurField('item_code')}
              disabled={!editable}
              placeholder="Code"
            />
          </div>

          {/* Description */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'description' })} style={{ flex: 1 }}>
            <textarea
              className={styles.textarea}
              value={item.description || ''}
              onChange={event => this.onChangeField('description', event.target.value)}
              onFocus={() => this.setState({ focusedField: 'description' })}
              onBlur={() => this.onBlurField('description')}
              disabled={!editable}
              placeholder="Description"
            />
          </div>

          {/* Quantity */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'quantity' })} style={{ width: 80 }}>
            <input
              className={classnames(styles.input, styles.inputRight)}
              type="number"
              value={item.quantity || ''}
              onChange={event => this.onChangeField('quantity', event.target.value)}
              onFocus={event => {
                event.target.select();
                this.setState({ focusedField: 'quantity' });
              }}
              onBlur={() => this.onBlurField('quantity')}
              disabled={!editable}
              step="0.01"
            />
          </div>

          {/* Unit of Measure */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'unit_of_measure' })} style={{ width: 80 }}>
            <div className={styles.outlet}>
              {item.unit_of_measure || 'Item'}
            </div>
            <select
              className={styles.select}
              value={item.unit_of_measure || 'Item'}
              onChange={event => this.onChangeField('unit_of_measure', event.target.value)}
              onFocus={() => this.setState({ focusedField: 'unit_of_measure' })}
              onBlur={() => this.onBlurField('unit_of_measure')}
              disabled={!editable}
            >
              {['m', 'm2', 'Item', 'Hours', 'Days', 'No.', 'kg', 'L'].map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          {/* Unit Cost */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'unit_cost' })} style={{ width: 100 }}>
            <input
              className={classnames(styles.input, styles.inputRight)}
              type="number"
              value={item.unit_cost || ''}
              onChange={event => this.onChangeField('unit_cost', event.target.value)}
              onFocus={event => {
                event.target.select();
                this.setState({ focusedField: 'unit_cost' });
              }}
              onBlur={() => this.onBlurField('unit_cost')}
              disabled={!editable}
              step="0.01"
            />
          </div>

          {/* Total Exc. VAT (calculated) */}
          <div className={styles.cell} style={{ width: 100 }}>
            <div className={classnames(styles.outlet, styles.outletRight)}>
              {(() => {
                const qty = parseFloat(String(item.quantity || 0).replace(/,/g, '')) || 0;
                const unitCost = parseFloat(String(item.unit_cost || 0).replace(/,/g, '')) || 0;
                return this.formatCurrency((qty * unitCost).toFixed(2));
              })()}
            </div>
          </div>

          {/* VAT Rate % */}
          <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'tax_rate' })} style={{ width: 80 }}>
            <div className={styles.outlet}>
              {`${item.tax_rate !== undefined && item.tax_rate !== null ? item.tax_rate : 0}%`}
            </div>
            <select
              className={styles.select}
              value={item.tax_rate !== undefined && item.tax_rate !== null ? parseInt(item.tax_rate) : 0}
              onChange={event => this.onChangeField('tax_rate', parseInt(event.target.value))}
              onFocus={() => this.setState({ focusedField: 'tax_rate' })}
              onBlur={() => this.onBlurField('tax_rate')}
              disabled={!editable}
            >
              <option value={0}>0%</option>
              <option value={20}>20%</option>
            </select>
          </div>

          {/* Total Cost inc. VAT (calculated) */}
          <div className={styles.cell} style={{ width: 110 }}>
            <div className={classnames(styles.outlet, styles.outletRight, styles.outletBold)}>
              {this.formatCurrency(totalCost)}
            </div>
          </div>

          {/* Markup % */}
          {/* <div className={classnames(styles.cell, { [styles.cellFocused]: focusedField === 'markup_percentage' })} style={{ width: 90 }}>
            <input
              className={classnames(styles.input, styles.inputRight)}
              type="number"
              value={item.markup_percentage || ''}
              onChange={event => this.onChangeField('markup_percentage', event.target.value)}
              onFocus={event => {
                event.target.select();
                this.setState({ focusedField: 'markup_percentage' });
              }}
              onBlur={() => this.onBlurField('markup_percentage')}
              disabled={!editable}
              step="1"
            />
          </div> */}

          {/* Sell Price (calculated) */}
          {/* <div className={styles.cell} style={{ width: 100 }}>
            <div className={classnames(styles.outlet, styles.outletRight)}>
              {item.markup_percentage ? this.formatCurrency(totalSellPrice) : '-'}
            </div>
          </div> */}

          {/* Status */}
          <div className={classnames(styles.cell, this.getStatusColorClass(item.status), { [styles.cellFocused]: focusedField === 'status' })} style={{ width: 100 }}>
            <div className={styles.outlet}>
              {(item.status && item.status.toUpperCase()) || 'PENDING'}
            </div>
            <select
              className={styles.select}
              value={item.status || 'pending'}
              onChange={event => this.onChangeField('status', event.target.value)}
              onFocus={() => this.setState({ focusedField: 'status' })}
              onBlur={() => this.onBlurField('status')}
              disabled={!editable}
            >
              <option value="pending">PENDING</option>
              <option value="ordered">ORDERED</option>
              <option value="partial">PARTIAL</option>
              <option value="received">RECEIVED</option>
              <option value="cancelled">CANCELLED</option>
            </select>
          </div>

          {/* Actions */}
          {editable && (
            <div className={styles.cell} style={{ width: 80, pointerEvents: 'all' }}>
              <div className={styles.actionArea}>
                {item._pendingDelete ? (
                  <span
                    className={styles.actionIcon}
                    style={{ color: '#1890ff', fontSize: 12, cursor: 'pointer' }}
                    onClick={this.onClickUndoDelete}
                  >
                    Undo
                  </span>
                ) : (
                  <React.Fragment>
                    {item.is_ad_hoc && (
                      <SaveOutlined
                        className={styles.actionIcon}
                        style={{ color: '#52c41a', cursor: 'pointer' }}
                        onClick={() => this.setState({ showSaveToCatalog: true })}
                      />
                    )}
                    <DeleteOutlined
                      className={styles.actionIcon}
                      style={{ color: '#ff4d4f' }}
                      onClick={this.onClickDelete}
                    />
                  </React.Fragment>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {this.state.showSaveToCatalog && (
        <SaveToCatalogModal
          item={item}
          onClose={() => this.setState({ showSaveToCatalog: false })}
        />
      )}
      </React.Fragment>
    );
  }
}
